import React, { useState, useCallback, useEffect } from 'react'
import {
  X,
  User,
  Lock,
  Sun,
  Moon,
  LogOut,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  Palette,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import * as authApi from '@/features/auth/services/authApi'
import { clearAuthSession } from '@/lib/authToken'

type Tab = 'profile' | 'password' | 'theme'

interface LocalUser {
  id?: string
  email?: string
  name?: string
  role?: string
}

interface Props {
  open: boolean
  onClose: () => void
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function SectionHeading({ title, description, theme }: { title: string; description?: string; theme: ReturnType<typeof useTheme>['currentTheme'] }) {
  return (
    <div className="mb-1">
      <h3 className="text-sm font-semibold" style={{ color: theme.text }}>{title}</h3>
      {description && (
        <p className="text-xs mt-0.5 leading-relaxed" style={{ color: theme.mutedText }}>{description}</p>
      )}
    </div>
  )
}

const OnboardingProfileSheet: React.FC<Props> = ({ open, onClose }) => {
  const { isDarkMode, setIsDarkMode, currentTheme } = useTheme()
  const navigate = useNavigate()

  const [user, setUser] = useState<LocalUser | null>(null)
  const [tab, setTab] = useState<Tab>('profile')
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [logoutLoading, setLogoutLoading] = useState(false)

  // Profile state
  const [profileName, setProfileName] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState('')
  const [profileSuccess, setProfileSuccess] = useState(false)

  // Password state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)

  useEffect(() => {
    if (open) {
      try {
        const raw = localStorage.getItem('user')
        if (raw) {
          const parsed = JSON.parse(raw) as LocalUser
          setUser(parsed)
          setProfileName(parsed.name || '')
        }
      } catch {}
    }
  }, [open])

  // Reset forms after the sheet close animation finishes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setTab('profile')
        setProfileError('')
        setProfileSuccess(false)
        setPasswordError('')
        setPasswordSuccess(false)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setShowCurrent(false)
        setShowNew(false)
        setShowConfirm(false)
        setShowLogoutConfirm(false)
      }, 300)
      return () => clearTimeout(t)
    }
  }, [open])

  const handleProfileSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = profileName.trim()
      if (!trimmed) { setProfileError('Name is required.'); return }
      if (trimmed.length < 2) { setProfileError('Name must be at least 2 characters.'); return }
      setProfileLoading(true)
      setProfileError('')
      setProfileSuccess(false)
      try {
        const updated = await authApi.updateProfile({ name: trimmed })
        try {
          const raw = localStorage.getItem('user')
          const existing = raw ? (JSON.parse(raw) as LocalUser) : {}
          localStorage.setItem('user', JSON.stringify({ ...existing, name: updated.name }))
        } catch {}
        setUser((prev) => (prev ? { ...prev, name: updated.name } : prev))
        setProfileSuccess(true)
        setTimeout(() => setProfileSuccess(false), 3500)
      } catch (err) {
        setProfileError(err instanceof Error ? err.message : 'Failed to update profile.')
      } finally {
        setProfileLoading(false)
      }
    },
    [profileName],
  )

  const handlePasswordSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!currentPassword) { setPasswordError('Current password is required.'); return }
      if (!newPassword) { setPasswordError('New password is required.'); return }
      if (newPassword.length < 8) { setPasswordError('New password must be at least 8 characters.'); return }
      if (!/[a-zA-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
        setPasswordError('New password must contain at least one letter and one number.')
        return
      }
      if (newPassword !== confirmPassword) { setPasswordError('Passwords do not match.'); return }
      setPasswordLoading(true)
      setPasswordError('')
      setPasswordSuccess(false)
      try {
        await authApi.changePassword({ current_password: currentPassword, password: newPassword, confirm_password: confirmPassword })
        setPasswordSuccess(true)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setTimeout(() => setPasswordSuccess(false), 3500)
      } catch (err) {
        setPasswordError(err instanceof Error ? err.message : 'Failed to change password.')
      } finally {
        setPasswordLoading(false)
      }
    },
    [currentPassword, newPassword, confirmPassword],
  )

  const handleLogout = useCallback(() => {
    setLogoutLoading(true)
    clearAuthSession()
    navigate('/login', { replace: true })
  }, [navigate])

  const initials = user?.name ? getInitials(user.name) : 'U'

  const inputStyle: React.CSSProperties = {
    backgroundColor: currentTheme.inputBackground,
    color: currentTheme.text,
    borderColor: currentTheme.border,
  }

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile', label: 'Profile', icon: <User size={14} /> },
    { key: 'password', label: 'Password', icon: <Lock size={14} /> },
    { key: 'theme', label: 'Appearance', icon: <Palette size={14} /> },
  ]

  function PasswordField({
    label,
    value,
    onChange,
    show,
    onToggle,
    placeholder = '••••••••',
    autoComplete = 'new-password',
  }: {
    label: string
    value: string
    onChange: (v: string) => void
    show: boolean
    onToggle: () => void
    placeholder?: string
    autoComplete?: string
  }) {
    return (
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.secondrytext }}>
          {label}
        </label>
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => { onChange(e.target.value); setPasswordError('') }}
            placeholder={placeholder}
            className="w-full rounded-2xl border px-4 py-3 pr-11 text-sm outline-none transition"
            style={inputStyle}
            autoComplete={autoComplete}
          />
          <button
            type="button"
            onClick={onToggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg"
            style={{ color: currentTheme.mutedText }}
            tabIndex={-1}
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          backgroundColor: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Account settings"
        className="fixed right-0 top-0 z-50 h-full w-full max-w-sm flex flex-col transition-transform duration-300 ease-out"
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          backgroundColor: currentTheme.secondary,
          borderLeft: `1px solid ${currentTheme.border}`,
          boxShadow: open ? '-12px 0 48px rgba(0,0,0,0.25)' : 'none',
        }}
      >
        {/* Sheet header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0"
          style={{ borderColor: currentTheme.border }}
        >
          <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: currentTheme.mutedText }}>
            Account Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl transition-colors"
            style={{ color: currentTheme.mutedText }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.hoverBackground)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* User identity card */}
        <div className="px-6 py-5 border-b flex-shrink-0" style={{ borderColor: currentTheme.border }}>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-base font-bold flex-shrink-0 select-none"
              style={{ backgroundColor: currentTheme.accent, color: '#fff' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate leading-tight" style={{ color: currentTheme.text }}>
                {user?.name || '—'}
              </p>
              <p className="text-sm truncate mt-0.5" style={{ color: currentTheme.secondrytext }}>
                {user?.email || '—'}
              </p>
              {user?.role && (
                <span
                  className="mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: currentTheme.hoverBackground, color: currentTheme.mutedText }}
                >
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="px-6 pt-4 pb-2 flex-shrink-0">
          <div
            className="flex rounded-2xl p-1 gap-1"
            style={{ backgroundColor: currentTheme.hoverBackground }}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold transition-all duration-200"
                style={{
                  backgroundColor: tab === t.key ? currentTheme.secondary : 'transparent',
                  color: tab === t.key ? currentTheme.text : currentTheme.mutedText,
                  boxShadow: tab === t.key ? currentTheme.boxShadow : 'none',
                }}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable tab content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar">

          {/* ── Profile tab ── */}
          {tab === 'profile' && (
            <form onSubmit={handleProfileSave} className="flex flex-col gap-5">
              <SectionHeading
                title="Personal information"
                description="Update your display name. Your email address is tied to your account and cannot be changed."
                theme={currentTheme}
              />

              {profileError && (
                <div
                  className="flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm"
                  style={{ backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText, border: `1px solid ${currentTheme.errorText}22` }}
                >
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>{profileError}</span>
                </div>
              )}
              {profileSuccess && (
                <div
                  className="flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm"
                  style={{ backgroundColor: currentTheme.successBackground, color: currentTheme.successText, border: `1px solid ${currentTheme.successText}22` }}
                >
                  <CheckCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>Profile updated successfully.</span>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.secondrytext }}>
                    Full name
                  </label>
                  <input
                    value={profileName}
                    onChange={(e) => { setProfileName(e.target.value); setProfileError('') }}
                    placeholder="Your name"
                    className="w-full rounded-2xl border px-4 py-3 text-sm outline-none transition"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.secondrytext }}>
                    Email address
                  </label>
                  <input
                    value={user?.email || ''}
                    readOnly
                    tabIndex={-1}
                    className="w-full rounded-2xl border px-4 py-3 text-sm outline-none cursor-not-allowed select-none"
                    style={{ ...inputStyle, opacity: 0.5 }}
                  />
                  <p className="mt-1.5 text-xs" style={{ color: currentTheme.mutedText }}>
                    Email cannot be changed.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={profileLoading}
                className="w-full flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.sendButtonPrimary, color: '#fff' }}
              >
                {profileLoading && <Loader2 size={15} className="animate-spin" />}
                {profileLoading ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          )}

          {/* ── Password tab ── */}
          {tab === 'password' && (
            <form onSubmit={handlePasswordSave} className="flex flex-col gap-5">
              <SectionHeading
                title="Change password"
                description="Enter your current password to verify your identity, then set a new one."
                theme={currentTheme}
              />

              {passwordError && (
                <div
                  className="flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm"
                  style={{ backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText, border: `1px solid ${currentTheme.errorText}22` }}
                >
                  <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>{passwordError}</span>
                </div>
              )}
              {passwordSuccess && (
                <div
                  className="flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm"
                  style={{ backgroundColor: currentTheme.successBackground, color: currentTheme.successText, border: `1px solid ${currentTheme.successText}22` }}
                >
                  <CheckCircle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>Password changed successfully.</span>
                </div>
              )}

              {/* Current password — visually separated */}
              <div
                className="rounded-2xl p-4 flex flex-col gap-1"
                style={{ backgroundColor: currentTheme.hoverBackground, border: `1px solid ${currentTheme.border}` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound size={14} style={{ color: currentTheme.mutedText }} />
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: currentTheme.mutedText }}>
                    Verify identity
                  </span>
                </div>
                <PasswordField
                  label="Current password"
                  value={currentPassword}
                  onChange={setCurrentPassword}
                  show={showCurrent}
                  onToggle={() => setShowCurrent((p) => !p)}
                  autoComplete="current-password"
                />
              </div>

              {/* New password section */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Lock size={14} style={{ color: currentTheme.mutedText }} />
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: currentTheme.mutedText }}>
                    New password
                  </span>
                </div>

                <PasswordField
                  label="New password"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNew}
                  onToggle={() => setShowNew((p) => !p)}
                />
                <PasswordField
                  label="Confirm new password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((p) => !p)}
                />

                <p className="text-xs leading-relaxed" style={{ color: currentTheme.mutedText }}>
                  Minimum 8 characters — must include at least one letter and one number.
                </p>
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="w-full flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.sendButtonPrimary, color: '#fff' }}
              >
                {passwordLoading && <Loader2 size={15} className="animate-spin" />}
                {passwordLoading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          )}

          {/* ── Appearance tab ── */}
          {tab === 'theme' && (
            <div className="flex flex-col gap-5">
              <SectionHeading
                title="Appearance"
                description="Choose how Zeph looks for you. This only affects your device."
                theme={currentTheme}
              />

              <div className="flex flex-col gap-3">
                {(
                  [
                    { label: 'Light mode', value: false, icon: <Sun size={20} />, desc: 'Clean and bright interface' },
                    { label: 'Dark mode', value: true, icon: <Moon size={20} />, desc: 'Easier on the eyes at night' },
                  ] as const
                ).map((option) => {
                  const isActive = isDarkMode === option.value
                  return (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setIsDarkMode(option.value)}
                      className="flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-all duration-200"
                      style={{
                        backgroundColor: isActive ? currentTheme.activeBackground : currentTheme.inputBackground,
                        borderColor: isActive ? currentTheme.accent : currentTheme.border,
                        boxShadow: isActive ? `0 0 0 2px ${currentTheme.accent}30` : 'none',
                      }}
                    >
                      <span style={{ color: isActive ? currentTheme.accent : currentTheme.mutedText }}>
                        {option.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: currentTheme.text }}>
                          {option.label}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: currentTheme.mutedText }}>
                          {option.desc}
                        </p>
                      </div>
                      {isActive && (
                        <CheckCircle size={16} style={{ color: currentTheme.accent, flexShrink: 0 }} />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Logout footer */}
        <div
          className="px-6 py-4 border-t flex-shrink-0"
          style={{ borderColor: currentTheme.border }}
        >
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-all"
            style={{
              color: currentTheme.errorText,
              borderColor: `${currentTheme.errorText}40`,
              backgroundColor: currentTheme.errorBackground,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <LogOut size={15} />
            Log out
          </button>
        </div>
      </div>

      {/* Logout confirmation dialog */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
            onClick={() => !logoutLoading && setShowLogoutConfirm(false)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-[340px] rounded-[24px] p-6 flex flex-col gap-5 shadow-2xl"
            style={{ backgroundColor: currentTheme.secondary, border: `1px solid ${currentTheme.border}` }}
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: currentTheme.errorBackground }}
              >
                <LogOut size={22} style={{ color: currentTheme.errorText }} />
              </div>
              <div>
                <h3 className="text-base font-semibold" style={{ color: currentTheme.text }}>
                  Log out of Zeph?
                </h3>
                <p className="text-sm mt-1" style={{ color: currentTheme.secondrytext }}>
                  You'll need to sign in again to continue.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={logoutLoading}
                className="flex-1 rounded-2xl border py-3 text-sm font-semibold transition disabled:opacity-50"
                style={{ backgroundColor: currentTheme.inputBackground, color: currentTheme.text, borderColor: currentTheme.border }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={logoutLoading}
                className="flex-1 flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-semibold transition disabled:opacity-60"
                style={{ backgroundColor: '#DC2626', color: '#fff' }}
              >
                {logoutLoading && <Loader2 size={14} className="animate-spin" />}
                {logoutLoading ? 'Logging out…' : 'Yes, log out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default React.memo(OnboardingProfileSheet)
